import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { BankAccountApiPayload, BankAccountService } from 'app/services/bank-account.service';
import { AcknowledgeOcrolusErrors, AddBankAccount, ClearBankAccountValues, CloneBankAccount, CreateBankStatementMonthlySummary, EditBankAccount, EditBankAccountAndSummaries, EditBankStatementMonthlySummaries, EditBankStatementMonthlySummariesReason, GetBankAccountValues, RemoveBankAccount, PullSummariesSync } from './bank-account.actions';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Alert } from 'app/models/dto/alert.model';
import {BankAccount} from 'app/models/dto/bank-account.model';

export class BankAccountStateModel {
    loading: boolean;
    cloning: boolean;
    bankAccounts: {
        [borrowerId: number]: BankAccountApiPayload;
    };
    clonedAccount: BankAccount | null;
    alert: null | Alert;
}

@Injectable()
@State<BankAccountStateModel>({
    name: 'bankAccount',
    defaults: {
        loading: false,
        cloning: false,
        bankAccounts: {},
        clonedAccount: null,
        alert: null
    }
})

export class BankAccountState {

    @Selector()
    static alert(state: BankAccountStateModel) {
        return state.alert;
    }

    @Selector()
    static cloning(state: BankAccountStateModel) {
        return state.cloning;
    }

    @Selector()
    static clonedAccount(state: BankAccountStateModel) {
        return state.clonedAccount;
    }

    @Selector()
    static bankValues(state: BankAccountStateModel) {
        const displayValueAliasList = [
            'balanceAvg3Month',
            'balanceAvg6Month',
            'totaledSalesAmountAvg3Month',
            'totaledSalesAmountAvg6Month',
            'daysNegativeTotal3Month',
            'daysNegativeTotal6Month',
            'salesDepositsCountAvg3Month',
            'salesDepositsCountAvg6Month',
            'salesDepositsCountAvg3Month',
            'salesDepositsCountAvg6Month',
            'daysNegativeAvg3Month',
            'daysNegativeAvg6Month',
            'batchDaysCountAvg3Month',
            'batchDaysCountAvg6Month',
            'nsfTransactionsAvg3Month',
            'nsfTransactionsAvg6Month',
            'nsfTransactionsTotal3Month',
            'nsfTransactionsTotal6Month',
            'creditsAmountAvg3Month',
            'creditsAmountAvg6Month',
            'creditsCountAvg3Month',
            'creditsCountAvg6Month',
            'batchDaysCountAvg3Month',
            'batchDaysCountAvg6Month',
            'endingStatementBalanceMin3Month',
            'endingStatementBalanceMin6Month',
            'endingStatementBalanceAvg3Month',
            'endingStatementBalanceAvg6Month',
            'numberMissingBankStatements3Month',
            'numberMissingBankStatements6Month'
        ];
        return (borrowerId: number) => {
            if (state.bankAccounts && state.bankAccounts[borrowerId] && state.bankAccounts[borrowerId].bankValues) {
                return state.bankAccounts[borrowerId].bankValues?.filter(bankAcct => displayValueAliasList.includes(bankAcct.alias));
            }
        };
    }
    @Selector()
    static bankAccounts(state: BankAccountStateModel) {
        return (borrowerId: number) => {
            if (state.bankAccounts && state.bankAccounts[borrowerId] && state.bankAccounts[borrowerId].accounts) {
                return state.bankAccounts[borrowerId].accounts;
            }
        };
    }

    @Selector()
    static bankAccountsStatus(state: BankAccountStateModel) {
        return (borrowerId: number) => {
            if (state.bankAccounts && state.bankAccounts[borrowerId]) {
                return state.bankAccounts[borrowerId];
            }
        };
    }

    @Selector()
    static alternateLenderTransactions(state: BankAccountStateModel) {
        return (borrowerId: number) => {
            const accounts = state.bankAccounts && state.bankAccounts[borrowerId] && state.bankAccounts[borrowerId].accounts;
            if (!accounts) {
                return [];
            }
            return accounts
                .map(account => account.alternate_lender_transactions).flat();
        };

    }

    constructor(
        private bankAccountService: BankAccountService
    ) { }


    @Action(GetBankAccountValues)
    getBankAccountValues(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { borrowerId }: GetBankAccountValues
    ) {
        const state = getState();
        if (state.bankAccounts && state.bankAccounts[borrowerId]) {
            return;
        }

        return this.fetchAccounts({ patchState, getState }, borrowerId);
    }

    @Action(ClearBankAccountValues)
    clearBankAccountValues(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { borrowerId }: ClearBankAccountValues
    ) {
        const bankAccounts = { ...getState().bankAccounts };
        delete bankAccounts[borrowerId];
        patchState({ bankAccounts });
    }

    @Action(AddBankAccount)
    addBankAccount(
        context: StateContext<BankAccountStateModel>,
        { newBankAccount }: AddBankAccount
    ) {
        return this.bankAccountService
            .post(newBankAccount)
            .pipe(
              switchMap(() => this.fetchAccounts(context, newBankAccount.borrowerId)),
              catchError( error => {
                console.error(error);
                return of();
              }),
            );
    }

    @Action(EditBankAccount)
    editBankAccount(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { bankAccount, borrowerId }: EditBankAccount
    ) {
        return this.bankAccountService
            .put(bankAccount, borrowerId)
            .pipe(
                catchError(() => {
                    patchState({ alert: {
                        level: 'error',
                        message: 'Unable to edit account'
                    } });
                    return of();
                }),
                switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId, {
                        level: 'success',
                        message: 'Account edited successfully'
                    }))
            );
    }

    @Action(CloneBankAccount)
    cloneBankAccount(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { bankAccountId, borrowerId }: CloneBankAccount
    ) {
        patchState({ cloning: true });
        return this.bankAccountService
            .clone(bankAccountId, borrowerId)
            .pipe(
                catchError(() => {
                    patchState({ alert: {
                        level: 'error',
                        message: 'Unable to clone account'
                    }, cloning: false });
                    return of();
                }),
                tap((data) => {
                  if (data) {
                    patchState({ clonedAccount: data.account });
                  }
                }),
                switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId))
            );
    }

    @Action(RemoveBankAccount)
    removeBankAccount(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { bankAccountId, borrowerId }: RemoveBankAccount
    ) {
        return this.bankAccountService
        .softDelete(bankAccountId, borrowerId)
        .pipe(
            catchError(() => {
                patchState({ alert: { level: 'error', message: 'Unable to remove account' } });
                return of();
            }),
            tap(() => {
                patchState({   alert: { level: 'success', message: 'Account removed successfully' } });
            }),
            switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId))
        );
    }

    @Action(EditBankStatementMonthlySummaries)
    editBankStatementMonthlySummaries(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { summaries, borrowerId }: EditBankStatementMonthlySummaries
    ) {
        return this.bankAccountService
        .updateSummaries(summaries, borrowerId)
        .pipe(
            catchError(() => {
                patchState({ alert: { level: 'error', message: 'Unable to edit bank monthly summaries' } });
                return of();
            }),
            tap(() => {
                patchState({ alert: { level: 'success', message: 'Monthly Summaries edited successfully' } });
            }),
            switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId))
        );
    }

  @Action(EditBankStatementMonthlySummariesReason)
  editBankStatementMonthlySummariesReason(
    { patchState }: StateContext<BankAccountStateModel>,
    { borrowerId, bankAccountEditReason }: EditBankStatementMonthlySummariesReason
  ) {
    return this.bankAccountService
      .sendEditReason(borrowerId, bankAccountEditReason)
      .pipe(
        catchError(() => {
          patchState({ alert: { level: 'error', message: 'Unable to edit bank monthly summaries' } });
          return of();
        }),
      );
  }

    @Action(EditBankAccountAndSummaries)
    editBankAccountAndSummaries(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { bankAccount, borrowerId }: EditBankAccountAndSummaries
    ) {
        if (!bankAccount.bank_statement_monthly_summaries) {
            return;
        }
        return forkJoin([
            this.bankAccountService.put(bankAccount, borrowerId),
            this.bankAccountService.updateSummaries(bankAccount.bank_statement_monthly_summaries, borrowerId)
        ])
        .pipe(
            catchError(() => {
                patchState({ alert: { level: 'error', message: 'Error in Editing Account And BankSummaries' } });
                return of();
            }),
            tap(() => {
                patchState({   alert: { level: 'success', message: 'Account and Bank Summaries Edited Successfully' } });
            }),
            switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId))
        );
    }

    @Action(CreateBankStatementMonthlySummary)
    createBankStatementMonthlySummary(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { summary, borrowerId }: CreateBankStatementMonthlySummary
    ) {
        return this.bankAccountService.createBankStatementMonthlySummary(summary, borrowerId)
        .pipe(
            catchError(() => {
                patchState({ alert: { level: 'error', message: 'Error in Creating Bank Statement Monthly Summary' } });
                return of();
            }),
            tap(() => {
                patchState({   alert: { level: 'success', message: 'Bank Statement Monthly Summary Created Successfully' } });
            }),
            switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId))
        );
    }

    @Action(AcknowledgeOcrolusErrors)
    acknowledgeOcrolusErrors(
        { getState, patchState }: StateContext<BankAccountStateModel>,
        { borrowerId }: AcknowledgeOcrolusErrors
    ) {
        return this.bankAccountService.acknowledgeOcrolusErrors(borrowerId)
        .pipe(
            catchError(() => {
                patchState({ alert: { level: 'error', message: 'Error In Acknowledging Document Errors.' } });
                return of();
            }),
            tap(() => {
                const bankAccounts = { ...getState().bankAccounts };
                bankAccounts[borrowerId].hasOcrolusErrors = false;
                patchState({ bankAccounts, alert: { level: 'success', message: 'Document Errors Successfully Acknowledged'}});
            }),
            switchMap(() => this.fetchAccounts({ patchState, getState }, borrowerId))
        );
    }

    @Action(PullSummariesSync)
    pullSummariesSync(
      { patchState }: StateContext<BankAccountStateModel>,
      { borrowerId }: PullSummariesSync
    ) {
      return this.bankAccountService
        .pullSummariesSync(borrowerId)
        .pipe(
          catchError(() => {
            patchState({ alert: { level: 'error', message: 'Unable to pull summaries' } });
            return of();
          }),
          tap(() => {
            patchState({ alert: { level: 'success', message: 'Monthly summary pull started successfully' } });
          })
        );
    }

    private fetchAccounts({ patchState, getState }, borrowerId, alert?) {
        patchState({ loading: true });
        return this.bankAccountService.get(borrowerId).pipe(
            catchError(() => {
                patchState({
                    loading: false,
                    cloning: false
                });
                return of();
            }),
            tap(data => {
                const bankAccounts = { ...getState().bankAccounts };
                data.accounts = data.accounts?.filter(account => !account.deleted);
                bankAccounts[borrowerId] = data;
                patchState({ bankAccounts, loading: false, alert, cloning: false });
            })
        );
    }
}
